import { useEffect, useState } from "react";
import { Download, Search, ShieldAlert, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";

const PAGE_SIZE = 50;

const ACTION_COLORS = {
  "login.success": { bg: "rgba(22,163,74,0.12)", color: "#86efac", border: "rgba(22,163,74,0.4)" },
  "login.fail": { bg: "rgba(220,38,38,0.12)", color: "#fca5a5", border: "rgba(220,38,38,0.4)" },
  "login.locked": { bg: "rgba(220,38,38,0.18)", color: "#fca5a5", border: "rgba(220,38,38,0.5)" },
  "twofa.enabled": { bg: "rgba(34,211,238,0.12)", color: "var(--cyan)", border: "rgba(34,211,238,0.4)" },
  "twofa.disabled": { bg: "rgba(217,70,239,0.12)", color: "var(--magenta)", border: "rgba(217,70,239,0.4)" },
  "twofa.recovery_used": { bg: "rgba(245,158,11,0.12)", color: "#fbbf24", border: "rgba(245,158,11,0.4)" },
  "expense.approved": { bg: "rgba(22,163,74,0.12)", color: "#86efac", border: "rgba(22,163,74,0.4)" },
  "expense.rejected": { bg: "rgba(220,38,38,0.12)", color: "#fca5a5", border: "rgba(220,38,38,0.4)" },
  "expense.paid": { bg: "rgba(34,211,238,0.12)", color: "var(--cyan)", border: "rgba(34,211,238,0.4)" },
  "expense.deleted": { bg: "rgba(220,38,38,0.18)", color: "#fca5a5", border: "rgba(220,38,38,0.5)" },
  "admin_expense.approved": { bg: "rgba(22,163,74,0.12)", color: "#86efac", border: "rgba(22,163,74,0.4)" },
  "admin_expense.rejected": { bg: "rgba(220,38,38,0.12)", color: "#fca5a5", border: "rgba(220,38,38,0.4)" },
  "admin_expense.paid": { bg: "rgba(34,211,238,0.12)", color: "var(--cyan)", border: "rgba(34,211,238,0.4)" },
  "admin_expense.deleted": { bg: "rgba(220,38,38,0.18)", color: "#fca5a5", border: "rgba(220,38,38,0.5)" },
};

function actionStyle(action) {
  return ACTION_COLORS[action] || {
    bg: "rgba(100,116,139,0.1)", color: "#cbd5e1", border: "var(--border)",
  };
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AuditLogs() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  // filters
  const [actionFilter, setActionFilter] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [text, setText] = useState("");

  const fetchActions = async () => {
    try {
      const { data } = await api.get("/audit-logs/actions");
      setActions(data || []);
    } catch { /* noop */ }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (actionFilter) params.action = actionFilter;
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (text.trim()) params.text = text.trim();
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("No se pudieron cargar los registros");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchActions(); }, []);
  useEffect(() => { fetchLogs(); /* eslint-disable-next-line */ }, [page]);

  const applyFilters = () => {
    setPage(0);
    fetchLogs();
  };

  const resetFilters = () => {
    setActionFilter(""); setStart(""); setEnd(""); setText("");
    setPage(0);
    setTimeout(fetchLogs, 0);
  };

  const downloadCSV = async () => {
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (start) params.start_date = start;
      if (end) params.end_date = end;
      if (text.trim()) params.text = text.trim();
      const res = await api.get("/audit-logs/export/csv", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit_log_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("No se pudo descargar el CSV");
    }
  };

  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div data-testid="audit-logs-page">
      <PageHeader
        subtitle="Registro de acciones críticas del sistema"
        title="Auditoría"
        actions={
          <button
            data-testid="audit-export-csv"
            onClick={downloadCSV}
            className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
            style={{ borderColor: "var(--green)", color: "var(--green)" }}
          >
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        }
      />

      <div className="px-6 lg:px-8 py-6 space-y-4">
        {/* Filters */}
        <div className="panel p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <span className="label-mono">// FILTROS</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <select
              data-testid="audit-filter-action"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="input-cyber px-3 py-2 text-sm"
            >
              <option value="" style={{ background: "#0a0f1d" }}>Todas las acciones</option>
              {actions.map((a) => (
                <option key={a.action} value={a.action} style={{ background: "#0a0f1d" }}>{a.label}</option>
              ))}
            </select>
            <input
              type="date"
              data-testid="audit-filter-start"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="input-cyber px-3 py-2 text-sm"
              placeholder="Desde"
            />
            <input
              type="date"
              data-testid="audit-filter-end"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="input-cyber px-3 py-2 text-sm"
              placeholder="Hasta"
            />
            <div className="relative md:col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                data-testid="audit-filter-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applyFilters(); }}
                placeholder="Buscar usuario, email o entidad..."
                className="input-cyber pl-10 pr-3 py-2 text-sm w-full"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <button
              data-testid="audit-apply-filters"
              onClick={applyFilters}
              className="btn-primary px-4 py-1.5 text-xs uppercase tracking-widest"
            >
              Aplicar
            </button>
            <button
              onClick={resetFilters}
              className="btn-ghost px-4 py-1.5 text-xs uppercase tracking-widest"
            >
              Limpiar
            </button>
            <span className="ml-auto text-xs text-slate-400">
              {total} registro{total !== 1 ? "s" : ""}
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="panel">
          {loading ? (
            <div className="p-10 text-center text-slate-400 text-sm">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              <ShieldAlert className="w-10 h-10 mx-auto mb-2 text-slate-600" />
              No hay registros con esos filtros.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: "rgba(15,23,42,0.7)" }}>
                    <th className="text-left px-4 py-3 label-mono">Fecha</th>
                    <th className="text-left px-4 py-3 label-mono">Acción</th>
                    <th className="text-left px-4 py-3 label-mono">Usuario</th>
                    <th className="text-left px-4 py-3 label-mono">Entidad</th>
                    <th className="text-left px-4 py-3 label-mono">IP</th>
                    <th className="text-left px-4 py-3 label-mono">Detalles</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const s = actionStyle(it.action);
                    const details = it.details || {};
                    const detailEntries = Object.entries(details);
                    return (
                      <tr
                        key={it.id}
                        data-testid={`audit-row-${it.id}`}
                        className="border-t row-hover"
                        style={{ borderColor: "var(--border)" }}
                      >
                        <td className="px-4 py-2.5 text-xs text-slate-300 whitespace-nowrap">{fmtDateTime(it.at)}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest"
                            style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}
                          >
                            {it.action_label}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-xs text-slate-200">{it.actor_name || "—"}</div>
                          <div className="text-[10px] text-slate-500">{it.actor_email || ""} {it.actor_role && `· ${it.actor_role}`}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="text-xs text-slate-200">{it.entity_label || it.entity_id || "—"}</div>
                          <div className="text-[10px] text-slate-500 label-mono">{it.entity_type}</div>
                        </td>
                        <td className="px-4 py-2.5 text-[10px] text-slate-400 font-mono">{it.ip || "—"}</td>
                        <td className="px-4 py-2.5">
                          {detailEntries.length === 0 ? (
                            <span className="text-[10px] text-slate-600">—</span>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              {detailEntries.slice(0, 3).map(([k, v]) => (
                                <div key={k} className="text-[10px] text-slate-400">
                                  <span className="text-slate-500">{k}:</span>{" "}
                                  <span className="text-slate-300">{String(v).slice(0, 50)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > PAGE_SIZE && (
            <div className="px-4 py-3 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
              <span className="text-xs text-slate-400">
                Página {page + 1} de {lastPage + 1}
              </span>
              <div className="flex items-center gap-2">
                <button
                  data-testid="audit-prev-page"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="btn-ghost p-1.5 rounded-sm disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  data-testid="audit-next-page"
                  onClick={() => setPage(Math.min(lastPage, page + 1))}
                  disabled={page >= lastPage}
                  className="btn-ghost p-1.5 rounded-sm disabled:opacity-40"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
